import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, Calendar, User, Clock, ArrowRight } from "lucide-react";
import api from "@/lib/api";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

function readTime(content) {
  const words = (content || "").split(/\s+/).length;
  return `${Math.max(1, Math.round(words / 200))} menit`;
}

function ArticleCard({ article }) {
  return (
    <Link
      to={`/artikel/${article.id}`}
      data-testid={`article-${article.id}`}
      className="bg-white rounded-xl overflow-hidden transition-all duration-300 block"
      style={{ border: "1px solid #f3f4f6" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div className="h-48 overflow-hidden">
        <img src={article.cover_image || "https://via.placeholder.com/600x400?text=Tozan"} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ color: "#198530", backgroundColor: "#eef9f0" }}>
            {article.category}
          </span>
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <Clock size={12} /> {readTime(article.content)}
          </span>
        </div>
        <h3 className="font-bold text-gray-900 mb-2 leading-snug line-clamp-2">{article.title}</h3>
        <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4">{article.excerpt}</p>
        <div className="flex items-center justify-between pt-3" style={{ borderTop: "1px solid #f3f4f6" }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: "#198530" }}>
              <User size={12} className="text-white" />
            </div>
            <span className="text-xs text-gray-500 truncate max-w-[140px]">{article.author}</span>
          </div>
          <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#198530" }}>
            Baca <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function JurnalArtikel({ defaultTab = "artikel" }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles", { params: { published_only: true } })
      .then((res) => setArticles(res.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="jurnal-artikel-page" style={{ paddingTop: "72px" }}>
      {/* Header */}
      <div
        className="py-12 text-white text-center"
        style={{ backgroundColor: "#198530" }}
      >
        <h1 className="text-4xl md:text-5xl font-bold">Jurnal &amp; Artikel</h1>
        <p className="mt-3 text-sm md:text-base" style={{ color: "#bbf7d0" }}>
          Tulisan, liputan, dan jurnal terbaru dari Yayasan Tozan
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <Tabs defaultValue={defaultTab} data-testid="jurnal-artikel-tabs">
          <TabsList className="mb-8 w-full sm:w-auto">
            <TabsTrigger value="artikel" data-testid="tab-artikel" className="flex items-center gap-2">
              <BookOpen size={16} /> Artikel
            </TabsTrigger>
            <TabsTrigger value="jurnal" data-testid="tab-jurnal" className="flex items-center gap-2">
              <BookOpen size={16} /> Jurnal
            </TabsTrigger>
          </TabsList>

          {/* Artikel Tab */}
          <TabsContent value="artikel" data-testid="artikel-content">
            <div className="mb-6">
              <p className="text-sm uppercase tracking-widest font-semibold mb-1" style={{ color: "#d4af37" }}>
                Informasi & Wawasan
              </p>
              <h2 className="text-2xl font-bold text-gray-900">Artikel Terbaru</h2>
            </div>
            {loading ? (
              <p className="text-gray-400 py-10 text-center">Memuat artikel...</p>
            ) : articles.length === 0 ? (
              <p className="text-gray-400 py-10 text-center">Belum ada artikel.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Jurnal Tab */}
          <TabsContent value="jurnal" data-testid="jurnal-content">
            <div className="text-center py-20">
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: "#eef9f0" }}
              >
                <BookOpen size={48} style={{ color: "#198530" }} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Jurnal Segera Hadir</h3>
              <p className="text-gray-500 max-w-md mx-auto leading-relaxed">
                Kami sedang mempersiapkan jurnal ilmiah dan publikasi akademik dari Yayasan Wakaf
                Tongku Azhar Nahdliyyin. Nantikan peluncurannya segera!
              </p>
              <div className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: "#d4af37", color: "#044712" }}>
                <Clock size={18} />
                Segera Hadir
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
